import { z } from 'zod';
import { uuid } from './common.js';
import { palletSize } from './packaging.js';
/**
 * Christmas packing: gift shops delivered to many addresses, where each address has departments.
 * One batch per department at an address. Scanning its pick list marks the gifts packed (taking them from the plan warehouse's stock) and asks how
 * many packages were used (pallets and kolli), which prints their labels. Scanning a package's label
 * books it: a pallet at once, a kolli alone or, with the other kolli of the address, on a shared
 * pallet booked when its last kolli is scanned. On the orders, the company name is the department
 * and the department field the leader. Orders are only read here; their status is handled elsewhere.
 */
export const XMAS_PALLET_SIZE_LABELS = { full: 'Helpalle', half: 'Halvpalle', quarter: 'Kvartpalle' };
export const xmasPackageKind = z.enum(['full', 'half', 'quarter', 'parcel']);
export const XMAS_PACKAGE_LABELS = { ...XMAS_PALLET_SIZE_LABELS, parcel: 'Kolli' };
export const xmasPlanSummaryDto = z.object({
    id: uuid,
    name: z.string(),
    shops: z.array(z.object({ id: uuid, name: z.string(), externalId: z.number().int().nullable() })),
    addresses: z.number().int(),
    batches: z.number().int(),
    orders: z.number().int(),
    packedBatches: z.number().int(),
    bookedAddresses: z.number().int(),
    createdAt: z.string(),
});
export const xmasReceiverDto = z.object({
    company: z.string().nullable(),
    name: z.string().nullable(),
    street1: z.string(),
    street2: z.string().nullable(),
    zip: z.string(),
    city: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
});
export const xmasBatchRowDto = z.object({
    id: uuid,
    number: z.number().int(),
    /** The pick list's barcode, unique across plans (XL000001). */
    code: z.string(),
    addressId: uuid,
    department: z.string(),
    orders: z.number().int(),
    packed: z.number().int(),
    /** Orders cancelled after they were put in the batch: leave them out. */
    cancelled: z.number().int(),
    /** Orders with only products marked "Pakkes ikke": nothing to pack, so not counted in orders. */
    skipped: z.number().int(),
    /** Units left for later and not packed yet ("Eftersendes"). */
    rest: z.number().int(),
    /** The rest pick list's barcode (XR000001). */
    restCode: z.string(),
    boxes: z.number().int(),
    status: z.enum(['open', 'packed']),
    printedAt: z.string().nullable(),
});
export const xmasShipmentDto = z.object({ id: uuid, number: z.string(), consignmentNumber: z.string().nullable(), status: z.string(), mode: z.string(), error: z.string().nullable() });
/** A package: a pallet of one department, or a kolli. Its label's barcode books it. */
export const xmasBoxDto = z.object({
    id: uuid,
    code: z.string(),
    kind: xmasPackageKind,
    seq: z.number().int(),
    weightG: z.number().int(),
    batchId: uuid,
    batchNumber: z.number().int(),
    department: z.string(),
    /** The shared pallet a kolli is on. */
    palletCode: z.string().nullable(),
    /** Where it is parked while it waits. */
    place: z.string().nullable(),
    /** Its booking, unless that was cancelled. */
    shipment: xmasShipmentDto.nullable(),
});
/** A shared pallet of kolli at an address; open until its last kolli is scanned. */
export const xmasPalletDto = z.object({
    id: uuid,
    code: z.string(),
    seq: z.number().int(),
    size: palletSize,
    weightG: z.number().int(),
    boxes: z.array(xmasBoxDto),
    closedAt: z.string().nullable(),
});
/**
 * Whether the warehouse's stock covers an address's unpacked gifts. Addresses claim stock in number
 * order. ready: covered. waiting: stock is enough on its own but earlier addresses claim it. short:
 * stock is not enough even alone. done: nothing left to pack.
 */
export const xmasStockDto = z.object({
    status: z.enum(['ready', 'waiting', 'short', 'done']),
    missing: z.array(z.object({ sku: z.string(), title: z.string(), needed: z.number().int(), available: z.number().int() })),
});
/**
 * One SKU of an address or batch; a bundle counts as its components. needed: units on the live orders.
 * packed: of those, already packed. packable: of the rest, what stock covers now after earlier addresses
 * and departments have claimed theirs; null until the plan has a warehouse. onHand: the warehouse's stock.
 */
export const xmasStockLineDto = z.object({
    sku: z.string(),
    title: z.string(),
    needed: z.number().int(),
    packed: z.number().int(),
    packable: z.number().int().nullable(),
    onHand: z.number().int().nullable(),
});
/** How far an address has come: the furthest stage reached. A stock shortage shows beside it, not instead of it. */
export const xmasAddressStage = z.enum(['short', 'ready', 'partly_printed', 'printed', 'partly_packed', 'awaiting_rest', 'packed', 'partly_booked', 'booked']);
export const XMAS_ADDRESS_STAGE_LABELS = {
    short: 'Mangler varer',
    ready: 'Klar',
    partly_printed: 'Delvist printet',
    printed: 'Printet',
    partly_packed: 'Delvist pakket',
    awaiting_rest: 'Afventer eftersending',
    packed: 'Pakket',
    partly_booked: 'Delvist booket',
    booked: 'Booket',
};
export const xmasAddressDto = z.object({
    id: uuid,
    number: z.number().int(),
    receiver: xmasReceiverDto,
    /** Booked once every batch is packed and every package has a booking. */
    status: z.enum(['open', 'booked']),
    /** Booked without waiting for what is left for later. */
    shipWithout: z.boolean(),
    /** Units left for later and not packed yet. */
    rest: z.number().int(),
    departments: z.number().int(),
    batches: z.array(xmasBatchRowDto),
    orders: z.number().int(),
    boxes: z.number().int(),
    boxesBooked: z.number().int(),
    pallets: z.array(xmasPalletDto),
    packages: z.array(xmasBoxDto),
    /** What is left before the address is done. */
    blockers: z.array(z.string()),
    /** Null until the plan has a warehouse and nothing has started, or when the address has no gifts. */
    stage: xmasAddressStage.nullable(),
    stock: xmasStockDto.nullable(),
    /** The carrier's tracking page per consignment; test bookings have none. */
    tracking: z.array(z.object({ consignmentNumber: z.string().nullable(), url: z.string() })),
    lines: z.array(xmasStockLineDto),
});
export const xmasPlanDto = xmasPlanSummaryDto.extend({
    warehouseId: uuid.nullable(),
    /** Only this location counts and is packed from; null means the whole warehouse. */
    locationId: uuid.nullable(),
    /** Products not packed here (donations and the like), by order-line SKU. */
    skipSkus: z.array(z.string()),
    /** Products left for later ("Eftersendes") on pick lists printed from now on. */
    laterSkus: z.array(z.string()),
    /** What has been left for later, per product: still to pack, packed since, and on hand (null for a sampak or without a warehouse). */
    restLines: z.array(z.object({ sku: z.string(), title: z.string(), open: z.number().int(), packed: z.number().int(), onHand: z.number().int().nullable() })),
    /** Every product on the plan's orders, to choose from. */
    products: z.array(z.object({ sku: z.string(), title: z.string(), quantity: z.number().int() })),
    carrierAccountId: uuid.nullable(),
    labelPrinterId: uuid.nullable(),
    smallLabelPrinterId: uuid.nullable(),
    documentPrinterId: uuid.nullable(),
    /** The warehouse the stock status is based on; null until the plan has one. */
    stockWarehouse: z.string().nullable(),
    addressList: z.array(xmasAddressDto.omit({ pallets: true, packages: true, lines: true })),
    /** The whole plan per SKU; packable is what is left to pack, up to what stock holds. */
    lines: z.array(xmasStockLineDto),
    /** Orders of the plan's shops that are not in a batch, with why. */
    issues: z.array(z.object({ orderId: uuid, orderNumber: z.string(), name: z.string().nullable(), reason: z.string() })),
    unbatched: z.number().int(),
});
/** A gift line left for later. deferred: on the list, its order not packed yet; open: packed without it; packed: added since. */
export const xmasLaterState = z.enum(['deferred', 'open', 'packed']);
const xmasPickEntry = z.object({ sku: z.string(), title: z.string(), variant: z.string().nullable(), quantity: z.number().int(), parts: z.array(z.object({ sku: z.string(), title: z.string(), quantity: z.number().int() })).nullable() });
export const xmasBatchDto = xmasBatchRowDto.extend({
    planId: uuid,
    address: xmasReceiverDto.extend({ number: z.number().int() }),
    gifts: z.array(z.object({ orderId: uuid, orderNumber: z.string(), name: z.string().nullable(), username: z.string().nullable(), leader: z.string(), sku: z.string(), title: z.string(), quantity: z.number().int(), packedAt: z.string().nullable(), cancelled: z.boolean(), skipped: z.boolean(), later: xmasLaterState.nullable() })),
    /** What to pick. A sampak is one entry with its parts; quantity is then the number of sampak. */
    pick: z.array(xmasPickEntry),
    /** What the rest pick list packs: the lines left for later and not packed yet. */
    restPick: z.array(xmasPickEntry),
    boxList: z.array(xmasBoxDto),
    lines: z.array(xmasStockLineDto),
});
/** The logged-in user's own printers (one computer per packing table), and what they can choose from. */
export const xmasMyPrintersDto = z.object({
    labelPrinterId: uuid.nullable(),
    smallLabelPrinterId: uuid.nullable(),
    documentPrinterId: uuid.nullable(),
    printers: z.array(z.object({ id: uuid, label: z.string() })),
});
export const saveXmasMyPrintersRequest = z.object({ labelPrinterId: uuid.nullable(), smallLabelPrinterId: uuid.nullable(), documentPrinterId: uuid.nullable() });
/** What a plan's settings choose from, for users who cannot see the warehouse, carrier and printing pages. */
export const xmasPlanOptionsDto = z.object({
    warehouses: z.array(z.object({ id: uuid, label: z.string() })),
    locations: z.array(z.object({ id: uuid, warehouseId: uuid, label: z.string() })),
    accounts: z.array(z.object({ id: uuid, label: z.string() })),
    printers: z.array(z.object({ id: uuid, label: z.string() })),
});
export const saveXmasSkipRequest = z.object({ skus: z.array(z.string().trim().min(1).max(100)).max(200) });
export const createXmasPlanRequest = z.object({ name: z.string().trim().min(1).max(120), shopExternalIds: z.array(z.number().int().positive()).max(50).default([]) });
export const saveXmasPlanRequest = z.object({
    name: z.string().trim().min(1).max(120),
    warehouseId: uuid.nullable(),
    /** Left out keeps what the plan has, so a tab with older code cannot clear it. */
    locationId: uuid.nullable().optional(),
    carrierAccountId: uuid.nullable(),
    labelPrinterId: uuid.nullable(),
    smallLabelPrinterId: uuid.nullable(),
    documentPrinterId: uuid.nullable(),
});
export const addXmasShopRequest = z.object({ externalId: z.number().int().positive() });
export const xmasScanRequest = z.object({ code: z.string().trim().min(1).max(60) });
/** The packages used for a batch: how many of each kind, and each one's weight. */
export const xmasRegisterRequest = z.object({
    rows: z.array(z.object({ kind: xmasPackageKind, count: z.number().int().min(1).max(500), weightG: z.number().int().min(1).max(2_000_000) })).min(1).max(4),
});
export const xmasBoxWeightRequest = z.object({ weightG: z.number().int().min(1).max(2_000_000) });
/** Booking a scanned package: a pallet as itself; a kolli alone or on the address's shared pallet. */
export const xmasShipRequest = z.object({ mode: z.enum(['package', 'pallet', 'alone']), size: palletSize.optional(), last: z.boolean().default(false) });
export const xmasBookPalletRequest = z.object({ size: palletSize.optional() });
export const xmasShipResultDto = z.object({ booked: z.boolean(), message: z.string(), consignmentNumber: z.string().nullable(), mode: z.string().nullable(), palletCode: z.string().nullable() });
export const saveXmasReceiverRequest = xmasReceiverDto.extend({
    street1: z.string().trim().min(1).max(200),
    zip: z.string().trim().min(3).max(10),
    city: z.string().trim().min(1).max(100),
});
export const xmasPrintRequest = z.object({ what: z.enum(['pick', 'labels', 'all', 'rest']).default('all') });
/** The rest pick list packed: the packages it went into with their new weight, and any new ones. */
export const xmasRestRequest = z.object({
    weights: z.array(z.object({ boxId: uuid, weightG: z.number().int().min(1).max(2_000_000) })).max(100).default([]),
    rows: z.array(z.object({ kind: xmasPackageKind, count: z.number().int().min(1).max(500), weightG: z.number().int().min(1).max(2_000_000) })).max(4).default([]),
});
export const xmasPlaceRequest = z.object({ place: z.string().trim().max(40).nullable() });
export const xmasShipWithoutRequest = z.object({ on: z.boolean() });
/** What a scan in the Christmas packing screens found. */
export const xmasScanResultDto = z.object({
    kind: z.enum(['gift', 'box', 'pallet', 'batch', 'rest', 'unknown']),
    planId: uuid.nullable(),
    batchId: uuid.nullable(),
    addressId: uuid.nullable(),
    message: z.string(),
    /** A pick list or rest pick list: what the package question shows. */
    batch: xmasBatchRowDto.extend({ where: z.string() }).nullable(),
    /** A package: what booking it needs, with the shared pallet a kolli would join. */
    box: xmasBoxDto.extend({ where: z.string(), openPallet: z.object({ code: z.string(), size: palletSize, boxes: z.number().int() }).nullable(), /** Why it cannot be booked yet: its address waits for what was left for later. */ waiting: z.string().nullable() }).nullable(),
});
//# sourceMappingURL=xmas.js.map